/**
 * `/signin` — the only screen a signed-out visitor can reach.
 *
 * Two states: the form, and "we sent it". The second one is not a toast, and
 * that is deliberate — "check your inbox" is an instruction the customer has to
 * act on in another application and then come back from, so it has to still be
 * on the screen when they do.
 *
 * The copy carries the one thing that is genuinely confusing about this
 * product's licensing, and it carries it *before* the customer hits it rather
 * than in an error afterwards: buying from us binds the key to the address on
 * the order, and buying anywhere else does not bind it to anything. So a
 * customer from a marketplace signs in to an address that owns nothing, and has
 * to be told, on this screen, that this is expected and what to do next.
 */

import { PageHeader } from "@/components/PageHeader";
import { Marker, Notice, Strip, StripBody, StripHead } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalError, portalApi, useApiMutation } from "@/lib/api";
import { useSession } from "@/lib/session";
import { MailCheck } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { LINK_TTL_MINUTES, looksLikeEmail } from "../../shared/portal";

export function SignInRoute() {
  const { state } = useSession();
  const [params] = useSearchParams();
  const next = params.get("next");

  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  const request = useApiMutation(
    (input: { email: string }) => portalApi.requestLink(input.email, next),
    {
      // The screen renders the failure itself, right under the field the
      // customer is looking at. A toast would put the sentence in the corner
      // and take it away again on a timer.
      suppressToast: true,
      onSuccess: (result, input) => {
        setSentTo(input.email);
        setDevLink(result.dev_link ?? null);
      },
    },
  );

  // Already signed in — arriving here means a stale tab or a bookmarked link.
  // Bounce rather than offering to sign in again on top of a live session.
  if (state?.signed_in) return <Navigate to={next ?? "/"} replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!looksLikeEmail(trimmed)) return;
    request.mutate({ email: trimmed });
  }

  if (sentTo) {
    return (
      <SentPanel
        email={sentTo}
        devLink={devLink}
        onUseAnother={() => {
          setSentTo(null);
          setDevLink(null);
          request.reset();
        }}
      />
    );
  }

  const invalid = email.trim().length > 0 && !looksLikeEmail(email.trim());

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        code="ACC"
        label="Sign in"
        title={
          <>
            Your <Marker draw>licenses and keys</Marker>
          </>
        }
        description="No password. Give us the address you want to sign in with and we will email you a link."
      />

      <Strip>
        <StripHead title="Email address" as="h2" id="signin-head" />
        <StripBody>
          <form onSubmit={onSubmit} noValidate>
            <label htmlFor="signin-email" className="t-field block">
              Address
            </label>
            <Input
              id="signin-email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
              aria-invalid={invalid || undefined}
              aria-describedby="signin-hint"
              className="mt-2 h-10"
            />
            <p id="signin-hint" className="mt-2 text-[0.8125rem] leading-snug text-ink-2">
              If you bought from us directly, use the address on your order. If you bought somewhere
              else, any address works — you will add your key after signing in.
            </p>

            {request.error ? <RequestError error={request.error} /> : null}

            <Button
              type="submit"
              size="lg"
              disabled={request.isPending || !looksLikeEmail(email.trim())}
              className="mt-5 w-full"
            >
              {request.isPending ? "Sending…" : "Email me a sign-in link"}
            </Button>
          </form>
        </StripBody>
      </Strip>

      <p className="mt-6 text-center text-[0.8125rem] leading-relaxed text-ink-2">
        Signing in creates an account if you do not have one. It does not, on its own, give you a
        license — that comes from a purchase, or from adding a key you already hold.
      </p>
    </div>
  );
}

/**
 * Failures on this form are shown in place and are two different things.
 *
 * The rate limit is not really an error — the customer already has a working
 * link sitting in their inbox — so it is toned as the pen mark rather than as a
 * failure, and the sentence says where to look.
 */
function RequestError({ error }: { error: Error }) {
  const throttled = error instanceof PortalError && error.code === "rate_limited";
  return (
    <Notice
      tone={throttled ? "marker" : "hold"}
      label={throttled ? "Already sent" : "Could not send"}
      className="mt-4"
    >
      {error.message}
    </Notice>
  );
}

function SentPanel({
  email,
  devLink,
  onUseAnother,
}: {
  email: string;
  devLink: string | null;
  onUseAnother: () => void;
}) {
  // The tab is very likely in the background while the customer is in their
  // mail client. Say so in the title so the returning glance at the tab strip
  // is enough to find this page again.
  useEffect(() => {
    const previous = document.title;
    document.title = "Check your email — Downwind Studio";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        code="ACC"
        label="Sign in"
        title="Check your email"
        description={
          <>
            We sent a sign-in link to <span className="t-data text-ink">{email}</span>. It works
            once and expires in {LINK_TTL_MINUTES} minutes.
          </>
        }
      />

      <Strip tone="cleared">
        <StripBody className="flex items-start gap-4 pl-5">
          <MailCheck className="mt-0.5 size-5 shrink-0 text-cleared" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-[0.9375rem] leading-relaxed text-ink">
              Open it on this device if you can — the link signs in the browser that opens it.
            </p>
            <p className="mt-3 text-[0.875rem] leading-relaxed text-ink-2">
              Nothing yet? Check the spam folder, and give it a minute. Mail from a domain you have
              not written to before often takes the scenic route.
            </p>
          </div>
        </StripBody>
      </Strip>

      {devLink ? <DevLink url={devLink} /> : null}

      <div className="mt-6 text-center">
        <Button variant="link" onClick={onUseAnother}>
          Use a different address
        </Button>
      </div>
    </div>
  );
}

/**
 * Local development only. The Worker returns `dev_link` exclusively when
 * `PORTAL_DEV_LINKS=1` is set in `.dev.vars`, and the deployed Worker has no
 * way to set it — so this panel is unreachable in production by construction
 * rather than by a check here. See the header of `worker/email.ts`.
 */
function DevLink({ url }: { url: string }) {
  return (
    <Notice tone="marker" label="Development" className="mt-4">
      No mail provider is configured, so here is the link:{" "}
      <a href={url} className="t-data break-all text-ink underline underline-offset-4">
        {url}
      </a>
    </Notice>
  );
}
